import {useQueryShopCar, useRemoveShopCar} from "../../../api/orderShopcarDetail";
import {createContext, useCallback, useContext, useEffect, useReducer} from "react";
import {useCount} from "../../../hooks/useCount";
import ls from "../../../utils/ls";
import {calc} from "../../../utils";
import {initialState, reducer, setCartList, setCheckedAll, setLoading, setReload} from "./reducer";
import {useNavigateTo} from "../../../hooks/useNavigateTo";
import useNotice from "../../../hooks/useNotice";
import Dialog from "../../../publicComponent/Dialog";
import {useTranslation,getI18n} from "react-i18next";
import store from "../../store";
import {setCartCount} from "../../actions";

const Context = createContext(initialState);

const flatGoods = (cart) => {
  let goodsItemMap = [];
  
  cart.forEach((shop) => {
    goodsItemMap = goodsItemMap.concat(shop.goodsList);
  });
  return goodsItemMap;
};

function countHandle(item, cb) {
  const [count, addCount, subCount] = useCount(item.quantity, 99);
  
  /**
   * 加减 商品数量
   * @param type
   */
  const handle = (type) => {
    let res = count;
    if (type === 'add') {
      addCount();
      res += 1;
    } else {
      subCount();
      res -= 1;
    }
    let goods = { ...item };
    goods.quantity = res;
    if (cb) cb(goods);
  };
  
  return {
    count,
    handle
  };
}

function CartController() {
  const { state, dispatch } = useCartContext();
  const { checkedAll, cartList } = state;
  const { loading, getCart } = useQueryShopCar();
  const { removeSku } = useRemoveShopCar();
  const navigateTo = useNavigateTo();
  const notice = useNotice();
  const { t } = useTranslation();
  const lang = getI18n().language;
  
  /**
   * 获取购物车列表
   * @returns {Promise<void>}
   */
  const getCartList = async () => {
    let { data } = await getCart();
    if (data) {
      dispatch(setCartList(data.shopCarList));
      
      // 更新Header 购物车数量徽章
      let count = 0;
      data.shopCarList.forEach((shop) => {
        count += shop.goodsList.length;
      });
      store.dispatch(setCartCount(count));
    }
  };
  
  const uploadGoods = (goods) => {
    let arr = [...cartList];
    arr.forEach((shop) => {
      if (shop.shopId === goods.shopId) {
        shop.goodsList = shop.goodsList.map((item) => {
          if (item.shopcarDetailId === goods.shopcarDetailId) {
            return goods;
          } else {
            return item;
          }
        });
      }
    });
    dispatch(setCartList(arr));
  };
  
  /**
   * 选择商品
   * @param item
   */
  const checkedHandle = (item) => {
    let arr = [...cartList];
    
    if (item === 'all') {
      let _checkedAll = !checkedAll;
      
      arr = arr.map((shop) => {
        shop.goodsList.forEach((goods) => {
          goods.checked = _checkedAll;
        });
        return shop;
      });
      
    } else {
      arr = arr.map((shop) => {
        shop.goodsList.forEach((goods) => {
          if (goods.itemSkuId === item.itemSkuId) {
            goods.checked = !item.checked;
          }
        });
        return shop;
      });
    }
    
    let goodsItemMap = flatGoods(arr);
    let checkedGoods = goodsItemMap.filter(item => item.checked);
    
    let checkedGoodsCount = checkedGoods.length;
    
    dispatch(setCheckedAll(checkedGoodsCount === goodsItemMap.length));
    dispatch(setCartList(arr));
  };
  
  /**
   * 获取提交订单的参数
   */
  const checkout = () => {
    let checkedShop = [...cartList].map((shop) => {
      let res = { ...shop };
      res.goodsList = res.goodsList.filter(item => item.checked);
      if (res.goodsList.length > 0) {
        res.goodsList = res.goodsList.map((goods) => {
          return {
            itemId: goods.itemId,
            itemSkuId: goods.itemSkuId,
            mainPicView: goods.mainPic,
            preferPrice: goods.preferPrice || 0,
            price: goods.price,
            productName: goods.itemName,
            productEnName: goods.itemEnName,
            quantity: goods.quantity,
            shopcarDetailId: goods.shopcarDetailId,
            specName: goods.specName
          };
        });
      }
      return res;
    });
    
    const checkedGoodsList = flatGoods(checkedShop);
    
    if (checkedGoodsList.length <= 0) {
      return notice.error(t('请选择商品'));
    }
    
    ls.setConfirmGoodsList(checkedShop);
    navigateTo('/confirmOrder');
  };
  
  const getBillData = useCallback(() => {
    
    if (cartList.length <= 0) return {};
    
    let goodsItemMap = flatGoods(cartList);
    let cart = goodsItemMap.filter(item => item.checked);
    let quantity = 0;
    let price = 0;
    let transportPreferPrice = 0;
    cart.forEach((goods) => {
      quantity += goods.quantity;
      price += goods.quantity * goods.price;
      if (goods.transportPreferPrice) {
        transportPreferPrice += goods.transportPreferPrice;
      }
    });
    
    return {
      quantity,
      price: calc.add(price),
      transportPreferPrice: calc.add(transportPreferPrice),
      checkedGoodsList: cart
    };
    
  }, [cartList]);
  
  /**
   * 移除购物车商品
   * @param item
   */
  const removeGoods = (item) => {
    Dialog.confirm({
      content: t('确定要删除吗？'),
      onOk: async (onClose) => {
        let { data } = await removeSku({ ids: item.shopcarDetailId });
        if (data) {
          notice.success(t('操作成功'));
          getCartList();
        }
        onClose();
      }
    });
  };
  
  useEffect(() => {
    dispatch(setLoading(loading));
  }, [loading]);
  
  return {
    getBillData,
    checkedHandle,
    countHandle,
    uploadGoods,
    checkout,
    removeGoods,
    getCartList
  };
}

function useCartContext() {
  const { state, dispatch } = useContext(Context);
  
  return {
    state,
    dispatch,
  };
}

export {useCartContext, CartController};

export default function CartContext({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  
  return (
    <Context.Provider
      value={{ state, dispatch }}
      initialState={initialState}
      reducer={reducer}>
      {children}
    </Context.Provider>
  );
}
